import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { books } from '../data/books';
import '../styles/BookSection.css';

import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';

const SERIES_NAME = 'Sierra Justice Series';

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// --- SCROLLING TAG BELT (giant faint text behind the stage) ---
const TextTicker = ({ tags, paused }) => {
  const extendedTags = [...tags, ...tags, ...tags, ...tags];

  return (
    <div className="featuredBook-tickerWrap" aria-hidden="true">
      <motion.div
        className="featuredBook-tickerTrack"
        animate={paused ? undefined : { x: ['0%', '-50%'] }}
        transition={
          paused
            ? undefined
            : { ease: 'linear', duration: 30, repeat: Infinity }
        }
      >
        {extendedTags.map((tag, i) => (
          <div key={i} className="featuredBook-tickerItem">
            <span>{tag}</span>
            <span className="featuredBook-tickerSeparator">·</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const BookSection = () => {
  const reduceMotion = useReducedMotion();

  // The first book in the data array is the promoted / featured one.
  const featuredId = books && books.length ? books[0].id : null;
  const [activeId, setActiveId] = useState(featuredId);

  // Reading order (Book 1 → 3) derived from release dates, so numbering
  // stays correct regardless of the order the data happens to be authored in.
  const seriesOrder = useMemo(
    () =>
      [...books].sort((a, b) => {
        const da = parseDate(a.details?.releaseDate)?.getTime() ?? 0;
        const db = parseDate(b.details?.releaseDate)?.getTime() ?? 0;
        return da - db;
      }),
    []
  );

  const numberById = useMemo(() => {
    const map = {};
    seriesOrder.forEach((book, i) => {
      map[book.id] = i + 1;
    });
    return map;
  }, [seriesOrder]);

  if (!books || books.length === 0) return null;

  const total = books.length;
  const activeBook = books.find((b) => b.id === activeId) || books[0];
  const activeNumber = numberById[activeBook.id];
  const isFeatured = activeBook.id === featuredId;

  const releaseDate = parseDate(activeBook.details?.releaseDate);
  const isUpcoming = releaseDate ? releaseDate.getTime() > Date.now() : false;

  const handleSelect = (book) => {
    setActiveId(book.id);
    logEvent(analytics, 'select_content', {
      content_type: 'series_book_select',
      item_id: book.id,
      item_name: book.title,
    });
  };

  const handleSynopsisClick = (book) => {
    logEvent(analytics, 'select_content', {
      content_type: 'book_synopsis',
      item_id: book.id,
      item_name: book.title,
    });
  };

  // Left/right arrows flip through the trilogy when the selector has focus.
  const handleSelectorKey = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      return;
    }
    e.preventDefault();
    const idx = seriesOrder.findIndex((b) => b.id === activeId);
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    handleSelect(seriesOrder[(idx + dir + total) % total]);
  };

  const stageTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.55, ease: [0.22, 1, 0.36, 1] };

  return (
    <section className="series-showcase" aria-label={`The ${SERIES_NAME}`}>
      <div className="series-inner">
        <header className="series-header">
          <span className="series-eyebrow">The Complete Trilogy</span>
          <h2 className="series-title">The {SERIES_NAME}</h2>
          <p className="series-tagline">
            One woman, three impossible choices. Follow environmental attorney
            Sierra Justice from the canyons of the American West to the shores of
            Greece.
          </p>
        </header>

        <div className="series-stage">
          <div className="series-stage-ticker" aria-hidden="true">
            <TextTicker
              key={activeBook.id}
              tags={activeBook.tags}
              paused={reduceMotion}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeBook.id}
              className="stage-grid"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -18 }}
              transition={stageTransition}
            >
              <div className="stage-cover-col">
                <a
                  href={activeBook.link}
                  className="stage-cover-link"
                  onClick={() => handleSynopsisClick(activeBook)}
                  aria-label={`${activeBook.title} — read synopsis and details`}
                >
                  <span className="stage-cover">
                    <img
                      src={activeBook.coverUrl}
                      alt={`${activeBook.title} cover`}
                      className="stage-cover-img"
                    />
                  </span>
                </a>
              </div>

              <div className="stage-info">
                <div className="stage-badges">
                  {isFeatured && (
                    <span className="stage-badge stage-badge--featured">
                      Featured Novel
                    </span>
                  )}
                  <span className="stage-badge">
                    Book {activeNumber} of {total}
                  </span>
                </div>

                <h3 className="stage-title">{activeBook.title}</h3>

                <div className="stage-meta">
                  {activeBook.details?.genre && (
                    <span className="stage-meta-item">
                      {activeBook.details.genre}
                    </span>
                  )}
                  {releaseDate && (
                    <span className="stage-meta-item">
                      {isUpcoming ? 'Coming' : 'Released'}{' '}
                      {activeBook.details.releaseDate}
                    </span>
                  )}
                </div>

                <p className="stage-description">{activeBook.description}</p>

                {activeNumber > 1 && (
                  <p className="stage-series-note">
                    Continues the {SERIES_NAME} — new readers should start with{' '}
                    <strong>{seriesOrder[0].title}</strong>.
                  </p>
                )}

                <div className="stage-actions">
                  <a
                    href={activeBook.link}
                    className="stage-cta"
                    onClick={() => handleSynopsisClick(activeBook)}
                  >
                    Read Synopsis &amp; Details
                    <span className="stage-cta-arrow" aria-hidden="true">
                      →
                    </span>
                  </a>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div
          className="series-selector"
          role="group"
          aria-label="Choose a book in the series"
          onKeyDown={handleSelectorKey}
        >
          {seriesOrder.map((book) => {
            const n = numberById[book.id];
            const selected = book.id === activeId;

            return (
              <button
                key={book.id}
                type="button"
                className={`selector-item ${selected ? 'is-active' : ''}`}
                aria-pressed={selected}
                aria-label={`Book ${n}: ${book.title}`}
                onClick={() => handleSelect(book)}
              >
                <span className="selector-thumb">
                  <img
                    src={book.coverUrl}
                    alt=""
                    aria-hidden="true"
                    className="selector-thumb-img"
                  />
                </span>
                <span className="selector-label">
                  <span className="selector-book-num">Book {n}</span>
                  <span className="selector-book-title">{book.title}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BookSection;
